import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AccessLevel } from "../portal-auth";
import {
  createShareLink,
  deleteShareLink,
  deleteShareResult,
  getShareResult,
  listShareLinks,
  type ShareLinkItem,
} from "./api";
import "./share.css";

export interface ShareConfigField {
  name: string;
  label: string;
  type: "select" | "text";
  options?: { value: string; label: string }[];
  required?: boolean;
}

export interface ShareLinksManagerProps {
  toolSlug: string;
  access: AccessLevel | null;
  configFields?: ShareConfigField[];
  /** Max link duration in weeks (default 4) */
  maxWeeks?: number;
  /** Override intro paragraph */
  introText?: string;
  /** Override expiry notice */
  expiryNotice?: string;
  /** Korte tekst achter de “Voltooid”-badge in de tabel (bijv. avontuurlabel). */
  renderStatus?: (data: Record<string, unknown> | null) => string | null;
  /** Uitgebreide weergave van het resultaat in het modal. */
  renderResult?: (data: Record<string, unknown> | null) => ReactNode;
  demoLinks?: ShareLinkItem[];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function configLabel(
  config: Record<string, unknown> | null,
  fields?: ShareConfigField[],
): string {
  if (!config || !fields?.length) return "—";
  return fields
    .map((f) => {
      const val = config[f.name];
      if (val == null) return null;
      const opt = f.options?.find((o) => o.value === val);
      return opt?.label ?? String(val);
    })
    .filter(Boolean)
    .join(", ");
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function ShareLinkUrlField({ url }: { url: string }) {
  return (
    <input
      type="text"
      className="share-manager__url-input"
      value={url}
      readOnly
      spellCheck={false}
      aria-label="Deellink"
      title={url}
      onFocus={(e) => e.currentTarget.select()}
    />
  );
}

export function ShareLinksManager({
  toolSlug,
  access,
  configFields = [],
  maxWeeks = 4,
  introText,
  expiryNotice,
  renderStatus,
  renderResult,
  demoLinks = [],
}: ShareLinksManagerProps) {
  const demoMode = access !== "PAID";
  const [links, setLinks] = useState<ShareLinkItem[]>(demoMode ? demoLinks : []);
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [configValues, setConfigValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of configFields) {
      init[f.name] = f.options?.[0]?.value ?? "";
    }
    return init;
  });
  const [resultModal, setResultModal] = useState<{
    linkId: string;
    data: Record<string, unknown> | null;
    completedAt: string;
  } | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const loadLinks = useCallback(async () => {
    if (demoMode) return;
    setLoading(true);
    setError(null);
    try {
      setLinks(await listShareLinks(toolSlug));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [demoMode, toolSlug]);

  useEffect(() => {
    void loadLinks();
  }, [loadLinks]);

  const onCreate = async () => {
    if (demoMode) return;
    setCreating(true);
    setError(null);
    try {
      const config: Record<string, unknown> = {};
      for (const f of configFields) {
        const val = configValues[f.name]?.trim();
        if (f.required && !val) {
          throw new Error(`Vul ${f.label.toLowerCase()} in.`);
        }
        if (val) config[f.name] = val;
      }
      await createShareLink({
        toolSlug,
        code: code.trim() || undefined,
        config: Object.keys(config).length ? config : undefined,
        weeks: maxWeeks,
      });
      setCode("");
      await loadLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Aanmaken mislukt");
    } finally {
      setCreating(false);
    }
  };

  const onCopy = async (linkId: string, url: string) => {
    const ok = await copyText(url);
    if (!ok) return;
    setCopiedLinkId(linkId);
    window.setTimeout(() => {
      setCopiedLinkId((current) => (current === linkId ? null : current));
    }, 1500);
  };

  const onDelete = async (id: string) => {
    if (demoMode) return;
    if (!window.confirm("Weet je zeker dat je deze link wilt verwijderen?")) return;
    try {
      await deleteShareLink(id);
      await loadLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verwijderen mislukt");
    }
  };

  const onViewResult = async (link: ShareLinkItem) => {
    if (demoMode && link.result) {
      setResultModal({
        linkId: link.id,
        data: link.result.data,
        completedAt: link.result.completedAt,
      });
      return;
    }
    try {
      const result = await getShareResult(link.id);
      setResultModal({
        linkId: link.id,
        data: result.data,
        completedAt: result.completedAt,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Resultaat laden mislukt");
    }
  };

  const onDeleteResult = async (linkId: string) => {
    if (demoMode) return;
    if (!window.confirm("Resultaat verwijderen? De link blijft bestaan.")) return;
    try {
      await deleteShareResult(linkId);
      setResultModal(null);
      await loadLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verwijderen mislukt");
    }
  };

  const emptyMessage = useMemo(
    () => (demoMode ? "Geen demo-links." : "Nog geen links aangemaakt."),
    [demoMode],
  );
  const hasConfig = configFields.length > 0;
  // ponytail: code column hidden temporarily (was 6/5)
  const tableColCount = hasConfig ? 5 : 4;

  return (
    <div className="share-manager">
      <p className="share-manager__intro">
        {introText ??
          "Maak een link om het spel met een client te delen. Je ziet hier of ze klaar zijn en welk avontuur ze speelden."}
      </p>
      <p className="share-manager__notice">
        {expiryNotice ??
          "Links vervallen automatisch 4 weken na aanmaken. Resultaten worden 2 weken na vervaldatum verwijderd."}
      </p>

      {demoMode ? (
        <div className="share-manager__demo-banner" role="status">
          <strong>Demo-weergave.</strong> Met een betaalde licentie kun je echte links
          aanmaken en resultaten van clients ontvangen.
        </div>
      ) : null}

      {!demoMode ? (
        <form
          className="share-manager__form"
          onSubmit={(e) => {
            e.preventDefault();
            void onCreate();
          }}
        >
          {configFields.map((field) => (
            <div className="share-manager__field" key={field.name}>
              <label htmlFor={`share-${field.name}`}>{field.label}</label>
              {field.type === "select" ? (
                <select
                  id={`share-${field.name}`}
                  value={configValues[field.name] ?? ""}
                  onChange={(e) =>
                    setConfigValues((v) => ({ ...v, [field.name]: e.target.value }))
                  }
                >
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`share-${field.name}`}
                  type="text"
                  value={configValues[field.name] ?? ""}
                  onChange={(e) =>
                    setConfigValues((v) => ({ ...v, [field.name]: e.target.value }))
                  }
                />
              )}
            </div>
          ))}

          {/* ponytail: code input temporarily hidden */}
          <p className="share-manager__privacy">
            Wij hebben geen verwerkersovereenkomst met jullie praktijk. Vul geen
            persoonlijke gegevens in!
          </p>

          <button type="submit" className="jari-btn jari-btn--primary" disabled={creating}>
            {creating ? "Bezig…" : "Link aanmaken"}
          </button>
        </form>
      ) : null}

      {error ? <p className="share-manager__error">{error}</p> : null}
      {loading ? <p>Laden…</p> : null}

      <div className="share-manager__table-wrap">
        <table className="share-manager__table">
          <thead>
            <tr>
              {hasConfig ? <th>Config</th> : null}
              <th>Link</th>
              <th>Geldig tot</th>
              <th>Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {links.length === 0 && !loading ? (
              <tr>
                <td colSpan={tableColCount}>{emptyMessage}</td>
              </tr>
            ) : null}
            {links.map((link) => (
              <tr key={link.id}>
                {hasConfig ? <td>{configLabel(link.config, configFields)}</td> : null}
                <td className="share-manager__url-cell">
                  <ShareLinkUrlField url={link.url} />
                </td>
                <td>
                  {formatDate(link.expiresAt)}
                  {link.expired ? (
                    <span className="share-manager__badge share-manager__badge--expired">
                      {" "}
                      verlopen
                    </span>
                  ) : null}
                </td>
                <td>
                  {link.result?.completed ? (
                    <span className="share-manager__badge share-manager__badge--ok">
                      Voltooid
                      {renderStatus
                        ? `: ${renderStatus(link.result.data) ?? ""}`
                        : null}
                    </span>
                  ) : (
                    <span className="share-manager__badge share-manager__badge--pending">
                      Nog niet voltooid
                    </span>
                  )}
                </td>
                <td>
                  <div className="share-manager__actions">
                    <button
                      type="button"
                      className="jari-btn share-manager__action-btn"
                      onClick={() => void onCopy(link.id, link.url)}
                    >
                      {copiedLinkId === link.id ? "Gekopieerd!" : "Kopieer"}
                    </button>
                    {link.result?.completed ? (
                      <button
                        type="button"
                        className="jari-btn share-manager__action-btn"
                        onClick={() => void onViewResult(link)}
                      >
                        Resultaat
                      </button>
                    ) : null}
                    {!demoMode ? (
                      <button
                        type="button"
                        className="jari-btn share-manager__action-btn"
                        onClick={() => void onDelete(link.id)}
                      >
                        Verwijder
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resultModal ? (
        <div
          className="share-manager__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-result-title"
          onClick={() => setResultModal(null)}
        >
          <div className="share-manager__modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="share-result-title">Resultaat</h3>
            <p className="share-manager__modal-meta">
              Voltooid op {formatDate(resultModal.completedAt)}
            </p>
            {renderResult ? renderResult(resultModal.data) : null}
            {!demoMode ? (
              <button
                type="button"
                className="jari-btn"
                onClick={() => void onDeleteResult(resultModal.linkId)}
              >
                Resultaat verwijderen
              </button>
            ) : null}
            <button type="button" className="jari-btn" onClick={() => setResultModal(null)}>
              Sluiten
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
